import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { clientService } from "../../../Services/client.service";
import { administrationService } from "../../../Services/administration.service";
import { representativesService } from "../../../Services/representatives.service";
import { errorHandling } from "../../../Services/error.handling";
import { formatToE164 } from "../../../Services/string.service";
import { getCompanyIdCookie } from "../../../Utils/companyIdCookie";

export const CLIENT_TYPE = { PF: "PF", PJ: "PJ" };
export const YES_NO = { YES: "S", NO: "N" };
export const DISABLED_FIELD_HEIGHT = { height: "calc(1.53em + 0.875rem + 2px)" };

export function useCustomerForm({
  clientId,
  isNewCustomer,
  onClientSaved,
  mainAddress,
  billingAddress,
}) {
  const { t } = useTranslation();

  const [clientName, setClientName] = useState("");
  const [clientTradeName, setClientTradeName] = useState("");
  const [doc, setDoc] = useState("");
  const [stateRegistration, setStateRegistration] = useState("");
  const [municipalRegistration, setMunicipalRegistration] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [phone1, setPhone1] = useState("");
  const [phone2, setPhone2] = useState("");
  const [responsibleEmail, setResponsibleEmail] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  const [clientType, setClientType] = useState(CLIENT_TYPE.PJ);
  const isPF = clientType === CLIENT_TYPE.PF;
  const [isSimpleTax, setIsSimpleTax] = useState(false);
  const [creditAnalysis, setCreditAnalysis] = useState("");
  const [dda, setDda] = useState("");
  const [installmentBooklet, setInstallmentBooklet] = useState("");
  const [servicePlan, setServicePlan] = useState("");
  const [restrictionType, setRestrictionType] = useState("");
  const [restrictionStartDate, setRestrictionStartDate] = useState("");
  const [restrictionEndDate, setRestrictionEndDate] = useState("");
  const [lastSuspensionDate, setLastSuspensionDate] = useState("");
  const [representative, setRepresentative] = useState("");
  const [salesperson, setSalesperson] = useState("");

  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastParams, setToastParams] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [plans, setPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [plansError, setPlansError] = useState(false);

  const [restrictions, setRestrictions] = useState([]);
  const [isLoadingRestrictions, setIsLoadingRestrictions] = useState(false);
  const [restrictionsError, setRestrictionsError] = useState(false);

  const [representatives, setRepresentatives] = useState([]);
  const [isLoadingRepresentatives, setIsLoadingRepresentatives] = useState(false);
  const [representativesError, setRepresentativesError] = useState(false);

  const [salespeople, setSalespeople] = useState([]);
  const [isLoadingSalespeople, setIsLoadingSalespeople] = useState(false);
  const [salespeopleError, setSalespeopleError] = useState(false);

  const showToast = useCallback((title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    let isMounted = true;

    if (!clientId || isNewCustomer) {
      setClientName("");
      setClientTradeName("");
      setDoc("");
      setStateRegistration("");
      setMunicipalRegistration("");
      setBirthDate("");
      setPhone1("");
      setPhone2("");
      setResponsibleEmail("");
      setCreatedAt("");
      setUpdatedAt("");

      setClientType(CLIENT_TYPE.PJ);
      setIsSimpleTax(false);
      setCreditAnalysis("");
      setDda("");
      setInstallmentBooklet("");
      setServicePlan("");
      setRestrictionType("");
      setRestrictionStartDate("");
      setRestrictionEndDate("");
      setLastSuspensionDate("");
      setRepresentative("");
      setSalesperson("");
      setFieldErrors({});
      return undefined;
    }

    const loadClient = async () => {
      setIsLoadingClient(true);
      setClientError(false);
      try {
        const response = await clientService.fetchClient(clientId);
        if (!isMounted) return;

        if (!response || response.error) {
          setClientError(true);
          return;
        }

        setClientName(response.clientName ?? "");
        setClientTradeName(response.clientTradeName ?? "");
        setDoc(response.doc ?? "");
        setStateRegistration(response.stateRegistration ?? "");
        setMunicipalRegistration(response.municipalRegistration ?? "");
        setBirthDate(response.birthDate ?? "");
        setPhone1(response.phone1 ?? "");
        setPhone2(response.phone2 ?? "");
        setResponsibleEmail(response.responsibleEmail ?? "");
        setCreatedAt(response.createdAt ?? "");
        setUpdatedAt(response.updatedAt ?? "");

        const docDigits = (response.doc ?? "").replace(/\D/g, "");
        setClientType(docDigits.length === 11 ? CLIENT_TYPE.PF : CLIENT_TYPE.PJ);
        // Campo vem como string "S"/"N" do GET, não booleano — Boolean("N")
        // daria true (qualquer string não-vazia é truthy em JS).
        setIsSimpleTax(response.simpleOption === "S");
        setCreditAnalysis(response.creditAnalysis ?? "");
        setDda(response.dda ?? "");
        setInstallmentBooklet(response.paymentBook ?? "");
        setServicePlan(response.servicePlanId ?? "");
        setRestrictionType(response.administrativeRestrictionId ?? "");
        setRestrictionStartDate(response.administrativeRestrictionDate ?? "");
        setRestrictionEndDate(response.administrativeRestrictionEndDate ?? "");
        setLastSuspensionDate(response.lastTemporarySuspensionDate ?? "");
        setRepresentative(response.representativeId ?? "");
        setSalesperson(response.salespersonId ?? "");
      } catch (error) {
        if (isMounted) setClientError(true);
      } finally {
        if (isMounted) setIsLoadingClient(false);
      }
    };

    loadClient();
    return () => {
      isMounted = false;
    };
  }, [clientId, isNewCustomer]);

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      setIsLoadingPlans(true);
      setPlansError(false);
      try {
        const response = await clientService.fetchPlans();
        if (isMounted) setPlans(Array.isArray(response) ? response : []);
      } catch (error) {
        if (isMounted) setPlansError(true);
      } finally {
        if (isMounted) setIsLoadingPlans(false);
      }
    };

    loadPlans();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadRestrictions = async () => {
      setIsLoadingRestrictions(true);
      setRestrictionsError(false);
      try {
        const response = await administrationService.fetchRestrictions();
        if (isMounted) setRestrictions(Array.isArray(response) ? response : []);
      } catch (error) {
        if (isMounted) setRestrictionsError(true);
      } finally {
        if (isMounted) setIsLoadingRestrictions(false);
      }
    };

    loadRestrictions();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadRepresentatives = async () => {
      setIsLoadingRepresentatives(true);
      setRepresentativesError(false);
      try {
        const response = await representativesService.fetchRepresentatives();
        if (isMounted) setRepresentatives(Array.isArray(response) ? response : []);
      } catch (error) {
        if (isMounted) setRepresentativesError(true);
      } finally {
        if (isMounted) setIsLoadingRepresentatives(false);
      }
    };

    loadRepresentatives();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!representative) {
      setSalespeople([]);
      return undefined;
    }

    const loadSalespeople = async () => {
      setIsLoadingSalespeople(true);
      setSalespeopleError(false);
      try {
        const response = await representativesService.fetchSalespeople(representative);
        if (isMounted) setSalespeople(Array.isArray(response) ? response : []);
      } catch (error) {
        if (isMounted) setSalespeopleError(true);
      } finally {
        if (isMounted) setIsLoadingSalespeople(false);
      }
    };

    loadSalespeople();
    return () => {
      isMounted = false;
    };
  }, [representative]);

  const handleRepresentativeChange = (value) => {
    setRepresentative(value);
    setSalesperson("");
  };

  const validateRequiredFields = () => {
    const errors = {};
    const docDigits = doc.replace(/\D/g, "");
    const expectedDocLength = isPF ? 11 : 14;

    if (!clientName.trim()) {
      errors.clientName = true;
    }
    if (docDigits.length !== expectedDocLength) {
      errors.doc = true;
    }
    if (!representative) {
      errors.representative = true;
    }
    if (!servicePlan) {
      errors.servicePlan = true;
    }
    if (!salesperson) {
      errors.salesperson = true;
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      scrollToTop();
      showToast(
        t("COMMON.ATTENTION"),
        t("CUSTOMER.PERSONAL_DATA.REQUIRED_FIELDS_ERROR"),
        "Warning"
      );
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateRequiredFields()) {
      return;
    }

    setIsSaving(true);

    const payload = {
      RegionalId: getCompanyIdCookie(),
      ClientName: clientName,
      ClientTradeName: isPF ? "" : clientTradeName,
      Doc: doc,
      RepresentativeId: representative || null,
      SellerId: salesperson || null,
      ServicePlanId: servicePlan || null,
      MasterUserEmail: null,
      MasterUserName: null,
      MasterUserAccessLevelId: null,
      StateRegistration: isPF ? "" : stateRegistration,
      MunicipalRegistration: isPF ? "" : municipalRegistration,
      SimpleTax: isSimpleTax ? "S" : "N",
      Public: null,
      Email: responsibleEmail,
      PhoneNumber: formatToE164(phone1),
      SecondaryPhoneNumber: formatToE164(phone2),
      Observation: null,
      OperationalObservation: null,
      BillingObservation: null,
      BirthDate: birthDate || null,
      PersonType: clientType,
      Dda: dda,
      CreditAnalysis: creditAnalysis,
      CreditAnalysisDate: null,
      PaymentBook: installmentBooklet,
      AdministrativeRestrictionId: restrictionType || null,
      AdministrativeRestrictionDate: restrictionStartDate || null,
      AdministrativeRestrictionEndDate: restrictionEndDate || null,
      LastTemporarySuspensionDate: null,
      AuthorizeEmail: null,
      AuthorizeSms: null,
      AuthorizeNegativeList: null,
      AuthorizeAutomaticRestriction: null,
      Whatsapp: null,
      ZipCode: mainAddress?.cep?.replace(/\D/g, "") || "",
      Street: mainAddress?.address || "",
      Number: mainAddress?.number || "",
      Neighborhood: mainAddress?.neighborhood || "",
      Complement: mainAddress?.complement || "",
      City: mainAddress?.city || "",
      State: mainAddress?.uf || "",
      SecondaryZipCode: billingAddress?.cep?.replace(/\D/g, "") || "",
      SecondaryStreet: billingAddress?.address || "",
      SecondaryNumber: billingAddress?.number || "",
      SecondaryNeighborhood: billingAddress?.neighborhood || "",
      SecondaryComplement: billingAddress?.complement || "",
      SecondaryCity: billingAddress?.city || "",
      SecondaryState: billingAddress?.uf || "",
    };

    try {
      const isNew = isNewCustomer || !clientId;
      const result = isNew
        ? await clientService.createClient(payload)
        : await clientService.updateClient(clientId, payload);

      if (!result || result.error) {
        const validationMessage = errorHandling.formatValidationErrors(result?.raw?.validations);
        const backendMessage = errorHandling.getErrorMessage(
          result,
          t("CUSTOMER.PERSONAL_DATA.UPDATE_ERROR")
        );
        scrollToTop();
        showToast(t("COMMON.ERROR"), validationMessage || backendMessage, "Danger");
        return;
      }

      showToast(t("COMMON.SUCCESS"), t("CUSTOMER.PERSONAL_DATA.UPDATED_SUCCESS"), "Success");

      const savedClientId = isNew ? result?.clientId ?? result?.ClientId : clientId;
      if (savedClientId) {
        onClientSaved?.(savedClientId, clientName);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleBillingClick = () => {
    showToast(t("COMMON.ATTENTION"), t("CUSTOMER.TAB_WIP"), "Warning");
  };

  return {
    t,
    clientId,
    isNewCustomer,
    clientName,
    setClientName,
    clientTradeName,
    setClientTradeName,
    doc,
    setDoc,
    stateRegistration,
    setStateRegistration,
    municipalRegistration,
    setMunicipalRegistration,
    birthDate,
    setBirthDate,
    phone1,
    setPhone1,
    phone2,
    setPhone2,
    responsibleEmail,
    setResponsibleEmail,
    createdAt,
    updatedAt,
    clientType,
    setClientType,
    isPF,
    isSimpleTax,
    setIsSimpleTax,
    creditAnalysis,
    setCreditAnalysis,
    dda,
    setDda,
    installmentBooklet,
    setInstallmentBooklet,
    servicePlan,
    setServicePlan,
    restrictionType,
    setRestrictionType,
    restrictionStartDate,
    setRestrictionStartDate,
    restrictionEndDate,
    setRestrictionEndDate,
    lastSuspensionDate,
    representative,
    salesperson,
    setSalesperson,
    isLoadingClient,
    clientError,
    isSaving,
    toastParams,
    setToastParams,
    fieldErrors,
    setFieldErrors,
    plans,
    isLoadingPlans,
    plansError,
    restrictions,
    isLoadingRestrictions,
    restrictionsError,
    representatives,
    isLoadingRepresentatives,
    representativesError,
    salespeople,
    isLoadingSalespeople,
    salespeopleError,
    showToast,
    handleRepresentativeChange,
    handleSave,
    handleBillingClick,
  };
}
